import { useSelector } from 'react-redux';
import type { RootState } from '../../../state/store';
import styled from 'styled-components';
import { Spinner } from 'react-bootstrap';
import { IoWarningOutline } from 'react-icons/io5';
import { useEffect, useState } from 'react';

interface AiAdvisorCtaProps {
  showLoadingIndicator: boolean;
  showError: boolean;
  onClick: () => void;
}

const AiAdvisorCta = ({
  showLoadingIndicator = false,
  showError = false,
  onClick = () => {}
}: AiAdvisorCtaProps) => {
  const theme = useSelector((state: RootState) => state.theme.mode);
  const [showErrorFallback, setShowErrorFallback] = useState(false);

  useEffect(() => {
    if (showError) {
      setShowErrorFallback(true);
      setTimeout(() => {
        setShowErrorFallback(false);
      }, 3000);
    }
  }, [showError]);

  const AiAssistantIcon = () => (
    <svg
      version="1.1"
      id="Layer_1"
      xmlns="http://www.w3.org/2000/svg"
      xmlnsXlink="http://www.w3.org/1999/xlink"
      x="0px"
      y="0px"
      width="283.5px"
      height="283.5px"
      viewBox="0 0 283.5 283.5"
      xmlSpace="preserve"
    >
      <g>
        <g>
          <path
            style={{ fill: '#010202' }}
            // eslint-disable-next-line max-len
            d="M72.4,206.7c36.2,7.1,43.2,14.1,50.3,50.3c0.2,1,1.6,1,1.8,0c7.1-36.2,14.1-43.2,50.3-50.3c1-0.2,1-1.6,0-1.8c-36.2-7.1-43.2-14.1-50.3-50.3c-0.2-1-1.6-1-1.8,0c-7.1,36.2-14.1,43.2-50.3,50.3C71.4,205.2,71.4,206.5,72.4,206.7z"
          />
          <path
            style={{ fill: '#010202' }}
            // eslint-disable-next-line max-len
            d="M52.1,137.5c17.4,3.4,20.7,6.7,24.1,24.1c0.1,0.4,0.7,0.4,0.8,0c3.4-17.4,6.7-20.7,24.1-24.1c0.4-0.1,0.4-0.7,0-0.8c-17.4-3.4-20.7-6.7-24.1-24.1c-0.1-0.4-0.7-0.4-0.8,0c-3.4,17.4-6.7,20.7-24.1,24.1C51.6,136.8,51.6,137.4,52.1,137.5z"
          />
          <path
            style={{ fill: '#010202' }}
            // eslint-disable-next-line max-len
            d="M92.5,96.1c61.9,12.4,75,25.5,87.4,87.4c0.5,2.4,3.9,2.4,4.4,0c12.4-61.9,25.5-75,87.4-87.4c2.4-0.5,2.4-3.9,0-4.4c-61.9-12.4-75-25.5-87.4-87.4c-0.5-2.4-3.9-2.4-4.4,0c-12.4,61.9-25.5,75-87.4,87.4C90.1,92.2,90.1,95.6,92.5,96.1z"
          />
        </g>
        <path
          style={{ fill: '#010202' }}
          // eslint-disable-next-line max-len
          d="M244.7,117.6c-2.5,0-4.6,2-4.6,4.6v101c0,25.1-23.9,48.6-49,48.6H69.3c-27.7,0-50.2-22.5-50.2-50.2V93.1C19.2,65.4,41.6,43,69.3,43h85.5c2.5,0,4.6-2,4.6-4.6v0c0-2.5-2-4.6-4.6-4.6H69.3C36.6,33.8,10,60.4,10,93.1v128.5c0,32.8,26.6,59.3,59.3,59.3h120.6c32.8,0,59.3-26.6,59.3-59.3v-99.5C249.2,119.6,247.2,117.6,244.7,117.6L244.7,117.6z"
        />
      </g>
    </svg>
  );

  const renderIcon = () => (
    <IconWrapper onClick={onClick} className={`ai-icon ${theme}`}>
      <AiAssistantIcon />
    </IconWrapper>
  );

  return (
    <Wrapper className="ai-advisor-cta">
      {showLoadingIndicator ? (
        <Spinner variant="border" className="spinner" />
      ) : showErrorFallback ? (
        <IoWarningOutline onClick={onClick} color="red" />
      ) : (
        renderIcon()
      )}
    </Wrapper>
  );
};

export default AiAdvisorCta;

const Wrapper = styled.span`
  height: 40px;
  width: 40px;
  cursor: pointer;
  margin: 0 7px;
  color: ${({ theme }) => theme.button_plain.text};
  border-radius: 8px;
  &:hover {
    color: ${({ theme }) => theme.button_plain.hover_text};
    background: ${({ theme }) => theme.button_plain.hover};
  }
  border-radius: 8px;
  display: flex;
  justify-content: center;
  padding: 2px 8px;
  padding: 0px;
  align-items: center;
  .spinner {
    font-size: 15px;
    height: 20px;
    width: 20px;
  }
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  cursor: pointer;

  svg {
    width: 100%;
    height: 100%;
    transition: filter 0.2s ease;
    margin-bottom: 4px; /* Offset for sparkle elements at top */
  }

  /* Light mode - keep original colors */
  &.light svg {
    filter: none;
  }

  /* Dark mode - invert colors to white */
  &.dark svg {
    filter: invert(1) brightness(1);
  }

  &:hover svg {
    opacity: 0.8;
  }
`;
